import type {
  MessageHandlerResult,
  MessageStats,
} from '@/services/abstract/poc/MessagingService';
import type { HeliaNode } from '@/types/poc/helia';
import type {
  GroupStatus,
  LibP2pWithServices,
  TurnConfig,
} from '@/types/poc/libp2p';

export interface P2PStackOptions {
  sixId: string;
  autoConnectLibp2p?: boolean;
  autoInitializeHelia?: boolean;
  autoInitializeMessaging?: boolean;
  bootstrapNodes: string[];
  swarmKey: string;
  fileListTopic: string;
  fileTransferTopic: string;
  onMessage?: MessageCallback;
  onStatusUpdate?: StatusUpdateCallback;
  onConnectionUpdate?: ConnectionUpdateCallback;
  turnConfig?: TurnConfig;
}

export type MessageCallback = (
  result: MessageHandlerResult,
  stats: MessageStats
) => void;

export type StatusUpdateCallback = (
  sixId: string,
  subscriberCounts: Record<string, number>
) => void;

export type ConnectionUpdateCallback = (
  sixId: string,
  connections: Array<{
    peerId: string;
    address: string;
    isDirect: boolean;
    isRelay: boolean;
    direction: 'inbound' | 'outbound';
  }>
) => void;

export interface NodeMetadata {
  status: GroupStatus;
  config: {
    groupId: string;
    relayAddresses: string[];
  };
  timers: {
    healthCheckInterval: NodeJS.Timeout | null;
    updateInterval: NodeJS.Timeout | null;
    reconnectInterval: NodeJS.Timeout | null;
    topicListBroadcastInterval: NodeJS.Timeout | null;
    sixIdAnnounceTimer: NodeJS.Timeout | null;
  };
  reconnectState: {
    attempts: number;
    maxAttempts: number;
    relayPeerId: string | null;
  };
  peerTracking: {
    connectionTimers: Map<string, NodeJS.Timeout>;
    blockedPeers: Set<string>;
    reconnectionIntervals: Map<string, NodeJS.Timeout>;
    connectionAttempts: Map<string, number>;
    lastConnectionAttempt: Map<string, number>;
  };
}

export interface P2PStackInstance {
  sixId: string;
  libp2pNode: LibP2pWithServices | null;
  heliaNode: HeliaNode | null;
  subscriptions: { cleanup: () => void } | null;
  isInitialized: boolean;
  fileListBroadcastInterval: NodeJS.Timeout | null;
  subscriberCheckTimer: NodeJS.Timeout | null;
  nodeMetadata: NodeMetadata | null;
  fileListTopic?: string;
  onStatusUpdate?: StatusUpdateCallback;
  onConnectionUpdate?: ConnectionUpdateCallback;
  meshHeartbeatInterval?: NodeJS.Timeout | null;
}

export interface InitializeStackResult {
  sixId: string;
  peerId: string;
  isSuccessful: boolean;
}

export abstract class P2PStackManager {
  abstract initializeStack(options: P2PStackOptions): Promise<InitializeStackResult>;
  abstract shutdownStack(sixId: string): Promise<void>;
  abstract shutdownAllStacks(): Promise<void>;
  abstract publishMessage(
    sixId: string,
    topic: string,
    data: Uint8Array | string
  ): Promise<void>;
  abstract getLibp2pNode(sixId: string): LibP2pWithServices | null;
  abstract getHeliaNode(sixId: string): HeliaNode | null;
  abstract getAllSixIds(): string[];
  abstract hasStack(sixId: string): boolean;
  abstract getConnectionInfo(sixId: string): Array<{
    peerId: string;
    address: string;
    isDirect: boolean;
    isRelay: boolean;
    direction: 'inbound' | 'outbound';
  }>;
  abstract reportBitswapStall(sixId: string): void;
}
